// Keep live caption nodes, focus and CSS animation clocks across state updates.
// This renderer owns only the translator root; it never touches another tool.
export const updateAutotranslatorDom = (root: HTMLElement, markup: string): void => {
    const template = document.createElement("template");
    template.innerHTML = markup;

    const key = (node: Node): string => {
        if (!(node instanceof Element)) return "";
        const marker = [...node.attributes].find((attribute) => attribute.name.startsWith("data-autotranslator-"));
        return marker ? `${node.nodeName}:${marker.name}:${marker.value}` : "";
    };
    const compatible = (left: Node, right: Node): boolean =>
        left.nodeType === right.nodeType && left.nodeName === right.nodeName && key(left) === key(right);

    const patch = (parent: Node, desired: Node): void => {
        const desiredKeys = new Set([...desired.childNodes].map(key).filter(Boolean));
        // Remove a dismissed drawer/row before ordering its surviving neighbors.
        // Moving a focused ancestor with insertBefore would blur its controls.
        for (const node of [...parent.childNodes]) {
            if (key(node) && !desiredKeys.has(key(node))) parent.removeChild(node);
        }
        const unused = new Set(parent.childNodes);
        let cursor = parent.firstChild;
        for (const next of [...desired.childNodes]) {
            const nextKey = key(next);
            const current = cursor && compatible(cursor, next)
                ? cursor
                : [...unused].find((node) => compatible(node, next) && (nextKey || node === cursor));
            if (!current) {
                parent.insertBefore(next.cloneNode(true), cursor);
                continue;
            }
            unused.delete(current);
            if (nextKey && current !== cursor) {
                const indentation: ChildNode[] = [];
                let candidate = cursor;
                while (candidate && candidate !== current && candidate.nodeType === 3 && !candidate.nodeValue?.trim()) {
                    indentation.push(candidate);
                    candidate = candidate.nextSibling;
                }
                if (candidate === current) {
                    for (const node of indentation) { parent.removeChild(node); unused.delete(node); }
                    cursor = current;
                }
            }
            if (current !== cursor) parent.insertBefore(current, cursor);
            cursor = current.nextSibling;
            if (current instanceof Element && next instanceof Element) {
                for (const attribute of [...current.attributes]) {
                    if (!next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
                }
                for (const attribute of [...next.attributes]) {
                    if (current.getAttribute(attribute.name) !== attribute.value) {
                        current.setAttribute(attribute.name, attribute.value);
                    }
                }
                if (current instanceof HTMLInputElement && next instanceof HTMLInputElement && current.value !== next.value) {
                    current.value = next.value;
                }
                patch(current, next);
            } else if (current.nodeValue !== next.nodeValue) {
                current.nodeValue = next.nodeValue;
            }
        }
        for (const node of unused) parent.removeChild(node);
    };
    patch(root, template.content);
};
